// ============================================================================
// Anaglyphic, Netherlands @ 2024.
// See README in the root project for more information.
// ============================================================================

import { MaybeRef, Ref, ref } from "vue";

// ============================================================================

/**
 * Composable to handle drag and drop on simple arrays.
 *
 * @note Naturally this composable doesn't apply an sort of styling on the
 * dragged elements themselves (except the drag image). It is expected that
 * draggable items have a `.over` class defined for how they visually should
 * look when being dragged over them.
 *
 * @param initial The initial starting value.
 */
export function useDragAndDrop<T>(initial: MaybeRef<T[]>) {
	const items = ref(initial);
	const draggedIndex = ref<number | null>(null);
	const isDragActive = ref<boolean>(false);

	// Start dragging and set up drag image
	function dragstartHandle(ev: DragEvent, id: number) {
		isDragActive.value = true;
		draggedIndex.value = id;
		ev.dataTransfer?.setData("text/plain", id.toString());
		ev.dataTransfer!.effectAllowed = "move";

		const node = ev.currentTarget as HTMLElement;
		const nodeRect = node.getBoundingClientRect();
		const offsetX = ev.clientX - nodeRect.left;
		const offsetY = ev.clientY - nodeRect.top;

		// You might think this is a hack, well actually this is how you're supposed to do it!
		// In order to set the image the DOM needs to render the cloned / clipped element
		// so that it can take a picture of it. It's hacky but it's the only legitimate way.
		const dragImage = node.cloneNode(true) as HTMLElement;
		dragImage.style.position = "absolute";
		dragImage.style.top = "-9999px";
		dragImage.style.borderRadius = "8px";
		document.body.appendChild(dragImage);
		ev.dataTransfer?.setDragImage(dragImage, offsetX, offsetY);

		setTimeout(() => document.body.removeChild(dragImage), 0);
	}

	// Handle dragging over an item
	function dragoverHandle(ev: DragEvent, targetIndex: number) {
		ev.preventDefault();
		if (draggedIndex.value !== targetIndex)
			(ev.currentTarget as HTMLElement).classList.add("over");
		ev.stopPropagation();
	}

	// Remove hover class when leaving an item
	function dragleaveHandle(ev: DragEvent) {
		ev.preventDefault();
		(ev.currentTarget as HTMLElement).classList.remove("over");
		ev.stopPropagation();
	}

	// Handle drop and reordering
	function dragdropHandle(ev: DragEvent, targetIndex: number) {
		dragleaveHandle(ev);

		const draggedIndexValue = Number(ev.dataTransfer?.getData("text/plain"));
		if (draggedIndexValue !== targetIndex) {
			const draggedItem = items.value.splice(draggedIndexValue, 1)[0];
			//@ts-ignore No idea why, but this not an error. It works.
			items.value.splice(targetIndex, 0, draggedItem);
		}
		isDragActive.value = false;
	}

	return {
		items,
		dragstartHandle,
		dragoverHandle,
		dragleaveHandle,
		dragdropHandle,
		isDragActive,
	};
}
